import fetch, { ApiClientError } from './fetch'
const client = fetch()

type mode = 'sign-in' | 'sign-up'

/** Firebase Identity Platform provider id for email/password ID tokens. */
export const PASSWORD_ID_TOKEN_PROVIDER = 'password' as const // NOSONAR(S2068)

export type tokenProviders = 'google' | 'microsoft' | typeof PASSWORD_ID_TOKEN_PROVIDER

export interface AuthUserResp {
  idTokenProvider: tokenProviders
  idToken: string
  email: string
  avatarUrl: string
  accessToken: string
  name: string
  displayName: string
  mode: mode
  hasInternalFeatureAccess: boolean
  expires: string
}

export interface AuthReq {
  email: string
  displayName?: string
  name?: string
  avatarUrl?: string
  mode?: mode
  idTokenProvider: tokenProviders
  idToken: string
}

export type AuthSignInReq = Omit<AuthReq, 'mode'> & {
  mode: 'sign-in'
}
export type AuthSignUpReq = Omit<AuthReq, 'mode'> & { mode: 'sign-up' }

// Details provided when we look for details for the currently logged in User
export interface MeUserResp {
  userId: number
  userEmail: string
  avatarUrl: string
  hasInternalFeatureAccess?: boolean
}

export const paths = {
  auth: '/auth'
}

// The backend doesn't set a distinguishing error code on these exceptions yet
// (server/users/exceptions.py), so we fall back to matching status + message.
// Status alone isn't reliable: a 404 can also mean a misconfigured API URL or
// gateway outage, and different call sites raise UserDoesNotExistError with
// different custom messages, so we match on a substring rather than exact text.
const matchesAuthError =
  (statusCode: number, messageSubstring: string) =>
  (error: unknown): boolean =>
    error instanceof ApiClientError &&
    error.status_code === statusCode &&
    error.message.toLowerCase().includes(messageSubstring)

export const isUserAlreadyExistsError = matchesAuthError(400, 'already exists')
export const isUserDoesNotExistError = matchesAuthError(404, 'does not exist')

export const signIn = async (user: AuthSignInReq): Promise<AuthUserResp> => {
  return await client.post<AuthUserResp>({
    path: paths.auth,
    data: user
  })
}

export const signUp = async (user: AuthSignUpReq): Promise<AuthUserResp> => {
  return await client.post<AuthUserResp>({
    path: paths.auth,
    data: user
  })
}

export const getCurrentLoggedUser = async (): Promise<MeUserResp> => {
  return await client.get<MeUserResp>({
    path: `/auth`
  })
}

/**
 * GCP Marketplace authentication types
 */
interface GCPMarketplaceAuthResponse {
  accessToken: string
  userId: number
  email: string
  name: string
  hasInternalFeatureAccess: boolean
  expires: string
}

export interface GCPMarketplaceSignUpResponse extends GCPMarketplaceAuthResponse {
  procurementAccountId: string
  clientId: number
}

export type GCPMarketplaceLoginResponse = GCPMarketplaceAuthResponse

/**
 * GCP Marketplace authentication API functions
 */
export const gcpMarketplaceSignUp = async (token: string): Promise<GCPMarketplaceSignUpResponse> => {
  return await client.post<GCPMarketplaceSignUpResponse>({
    path: '/auth/gcp-marketplace/signup',
    data: { token }
  })
}

export const gcpMarketplaceLogin = async (token: string): Promise<GCPMarketplaceLoginResponse> => {
  return await client.post<GCPMarketplaceLoginResponse>({
    path: '/auth/gcp-marketplace/login',
    data: { token }
  })
}
